<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'

const props = defineProps<{
  accountId: number
}>()

const { t, d } = useI18n()
const notifyStore = useNotifyStore()

interface SessionLocation {
  city?: string
  country?: string
  description?: string
}

interface Session {
  id: number
  created_at: string
  expires_at: string
  device?: string
  location?: SessionLocation | null
  validated: boolean
  current: boolean
}

interface SessionsResponse {
  data: Session[]
  total: number
  limit: number
  offset: number
}

const sessions = ref<Session[]>([])
const loading = ref(false)
const revoking = ref<number | null>(null)
const revokingAll = ref(false)

function parseDevice(ua?: string): { icon: string; label: string } {
  if (!ua) return { icon: 'mdi-web', label: t('dashboard.admin.users.detail.sessions.unknownDevice') }

  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua)
  const isTablet = /iPad|Tablet/i.test(ua)

  let browser = 'Browser'
  if (/Firefox/i.test(ua)) browser = 'Firefox'
  else if (/Edg/i.test(ua)) browser = 'Edge'
  else if (/Chrome/i.test(ua)) browser = 'Chrome'
  else if (/Safari/i.test(ua)) browser = 'Safari'

  let os = ''
  if (/Windows/i.test(ua)) os = 'Windows'
  else if (/Mac OS/i.test(ua)) os = 'macOS'
  else if (/Linux/i.test(ua)) os = 'Linux'
  else if (/Android/i.test(ua)) os = 'Android'
  else if (/iPhone|iPad/i.test(ua)) os = 'iOS'

  const label = os ? `${browser} on ${os}` : browser
  const icon = isTablet ? 'mdi-tablet' : isMobile ? 'mdi-cellphone' : 'mdi-laptop'
  return { icon, label }
}

function formatLocation(loc?: SessionLocation | null): string {
  if (!loc) return t('dashboard.admin.users.detail.sessions.unknownLocation')
  const parts = [loc.city, loc.country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : t('dashboard.admin.users.detail.sessions.unknownLocation')
}

function fetchSessions() {
  loading.value = true
  useAxios<never, SessionsResponse>('GET', `/account/${props.accountId}/sessions?limit=50`, true)
    .then((res) => {
      if (res?.data) {
        sessions.value = res.data.data
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'UserSessionsSection:fetch', reportNetworkErrors: false })
    })
    .finally(() => {
      loading.value = false
    })
}

function revokeSession(session: Session) {
  if (!confirm(t('dashboard.admin.users.detail.sessions.revokeConfirm'))) return

  revoking.value = session.id
  useAxios<never, never>('DELETE', `/account/${props.accountId}/sessions/${session.id}`, true)
    .then(() => {
      sessions.value = sessions.value.filter((s) => s.id !== session.id)
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.users.detail.sessions.revokeSuccess'),
        message: ''
      })
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'UserSessionsSection:revoke', reportNetworkErrors: false })
    })
    .finally(() => {
      revoking.value = null
    })
}

function revokeAllSessions() {
  if (!confirm(t('dashboard.admin.users.detail.sessions.revokeAllConfirm'))) return

  revokingAll.value = true
  useAxios<never, { revoked: number }>('DELETE', `/account/${props.accountId}/sessions`, true)
    .then((res) => {
      const count = res?.data?.revoked ?? 0
      fetchSessions()
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.users.detail.sessions.revokeAllSuccess', { count }),
        message: ''
      })
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'UserSessionsSection:revokeAll', reportNetworkErrors: false })
    })
    .finally(() => {
      revokingAll.value = false
    })
}

onMounted(() => {
  fetchSessions()
})
</script>

<template>
  <div class="sessions-section">
    <!-- Section Header -->
    <div class="section-header">
      <div class="header-icon">
        <v-icon icon="mdi-devices" size="20" />
      </div>
      <div class="header-text">
        <h3 class="header-title">{{ $t('dashboard.admin.users.detail.sessions.title') }}</h3>
      </div>
      <button
        v-if="sessions.length > 0"
        class="btn btn-revoke-all"
        :disabled="revokingAll"
        @click="revokeAllSessions"
      >
        <v-progress-circular v-if="revokingAll" indeterminate size="16" width="2" />
        <v-icon v-else icon="mdi-logout-variant" size="16" />
        <span>{{ $t('dashboard.admin.users.detail.sessions.revokeAll') }}</span>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="sessions-loading">
      <v-progress-circular indeterminate size="24" width="2" />
    </div>

    <!-- Empty State -->
    <div v-else-if="sessions.length === 0" class="sessions-empty">
      <v-icon icon="mdi-shield-check" size="20" />
      <span>{{ $t('dashboard.admin.users.detail.sessions.empty') }}</span>
    </div>

    <!-- Session List -->
    <div v-else class="sessions-list">
      <div
        v-for="session in sessions"
        :key="session.id"
        class="session-item"
        :class="{ 'session-current': session.current }"
      >
        <div class="session-icon">
          <v-icon :icon="parseDevice(session.device).icon" size="22" />
        </div>
        <div class="session-info">
          <div class="session-device">{{ parseDevice(session.device).label }}</div>
          <div class="session-meta">
            <span class="session-location">
              <v-icon icon="mdi-map-marker-outline" size="14" />
              {{ formatLocation(session.location) }}
            </span>
            <span class="session-date">
              {{ d(new Date(session.created_at), 'short') }}
            </span>
          </div>
        </div>
        <div class="session-status">
          <span v-if="session.current" class="status-badge status-current">
            <v-icon icon="mdi-check-circle" size="14" />
            {{ $t('dashboard.admin.users.detail.sessions.current') }}
          </span>
          <span
            v-else-if="session.validated"
            class="status-badge status-validated"
          >
            {{ $t('dashboard.admin.users.detail.sessions.validated') }}
          </span>
          <span v-else class="status-badge status-pending">
            {{ $t('dashboard.admin.users.detail.sessions.notValidated') }}
          </span>
        </div>
        <div class="session-actions">
          <button
            v-if="!session.current"
            class="btn btn-revoke"
            :disabled="revoking === session.id"
            @click="revokeSession(session)"
          >
            <v-progress-circular v-if="revoking === session.id" indeterminate size="16" width="2" />
            <v-icon v-else icon="mdi-logout" size="16" />
            <span>{{ $t('dashboard.admin.users.detail.sessions.revoke') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sessions-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;
  color: #00b0ff;
  flex-shrink: 0;
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

/* Loading & Empty */
.sessions-loading,
.sessions-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px;
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.9rem;
}

/* Session List */
.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.06);
  border-radius: 12px;

  &.session-current {
    border-color: rgba(76, 175, 80, 0.15);
    background: rgba(76, 175, 80, 0.04);
  }
}

.session-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  color: rgba(200, 218, 255, 0.6);
  flex-shrink: 0;
}

.session-info {
  flex: 1;
  min-width: 0;
}

.session-device {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 3px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
}

.session-location {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.session-status {
  flex-shrink: 0;
}

.session-actions {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-current {
  background: rgba(76, 175, 80, 0.1);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.status-validated {
  background: rgba(0, 176, 255, 0.08);
  color: #4fc3f7;
  border: 1px solid rgba(0, 176, 255, 0.15);
}

.status-pending {
  background: rgba(255, 152, 0, 0.08);
  color: #ffb74d;
  border: 1px solid rgba(255, 152, 0, 0.15);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-revoke {
  padding: 6px 14px;
  background: rgba(244, 67, 54, 0.08);
  color: #ef5350;
  border: 1px solid rgba(244, 67, 54, 0.15);

  &:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
  }
}

.btn-revoke-all {
  padding: 8px 16px;
  background: rgba(244, 67, 54, 0.08);
  color: #ef5350;
  border: 1px solid rgba(244, 67, 54, 0.15);

  &:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .header-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .header-icon {
    background: rgba(28, 110, 227, 0.1);
    border-color: rgba(28, 110, 227, 0.2);
    color: #1c6ee3;
  }

  .session-item {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.06);

    &.session-current {
      border-color: rgba(76, 175, 80, 0.15);
      background: rgba(76, 175, 80, 0.03);
    }
  }

  .session-icon {
    background: rgba(0, 0, 0, 0.04);
    color: rgba(15, 23, 42, 0.5);
  }

  .session-device {
    color: rgba(15, 23, 42, 0.9);
  }

  .session-meta {
    color: rgba(15, 23, 42, 0.4);
  }

  .sessions-loading,
  .sessions-empty {
    color: rgba(15, 23, 42, 0.4);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .session-item {
    flex-wrap: wrap;
  }

  .session-status,
  .session-actions {
    width: auto;
  }

  .section-header {
    flex-wrap: wrap;
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn {
    transition: none;
  }
}
</style>
